import useAftermarketPurchaseFromQuery from "@modules/purchase/warranty/hooks/useAftermarketPurchaseFromQuery";
import useSourceInfoFromQuery from "@modules/query-string/hooks/useSourceInfoFromQuery";
import { useRouter } from "next/router";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import { provinceFilters } from "@modules/purchase/warranty/hooks/useEligibleProvinces";
import useProvincesStatusBySourceList from "@modules/purchase/warranty/hooks/useProvincesStatusBySourceList";
import { getCondensedLookupVehicleName } from "@modules/vehicle/hooks/useVehicleLookup";
import { useNullableNumberMask } from "@common/hooks/useMask";
import useVehicleDisplayFuelTypes from "@modules/vehicle/hooks/useVehicleDisplayFuelTypes";
import { translate } from "@talladega/helpers";
import { useTranslation } from "next-i18next";
import { provinces } from "@talladega/types";
import { removeQueryParams } from "@modules/core/routing/routingHelpers";
import {
  quoteVehicleFormMode,
  WarrantySegmentationMode,
  WarrantySegmentationModeName,
} from "../types/WarrantySegmentationMode";
import { WarrantySegmentationFormProps } from "../types/WarrantySegmentationFormProps";
import WarrantyQuoteVehicleInfoForm from "./WarrantyQuoteVehicleInfoForm";
import WarrantyQuotePersonalInfoForm from "./WarrantyQuotePersonalInfoForm";

type Props = {
  modeState: [
    WarrantySegmentationMode,
    Dispatch<SetStateAction<WarrantySegmentationMode>>
  ];
};

const WarrantySegmentationForm = ({ modeState }: Props) => {
  const { t } = useTranslation(["common", "purchase"]);
  const [mode, setMode] = modeState;
  const router = useRouter();
  const { locale: rlocale } = router;
  const locale = rlocale || "en";

  // Keep useState for vehicleSearchMode up here to ensure it is not reset when user goes through different form modes.
  const [vehicleSearchMode, setVehicleSearchMode] = useState<"ymm" | "vin">(
    "ymm"
  );

  const switchMode = useCallback(
    (newMode: WarrantySegmentationMode) => {
      setMode(newMode);
    },
    [setMode]
  );

  const aftermarketPurchaseFromQuery = useAftermarketPurchaseFromQuery();
  const { source: querySource } = useSourceInfoFromQuery();

  const {
    firstName,
    lastName,
    email,
    phone,
    province: buyerProvince,
    vin: buyerVin,
    mileage,
    year,
    make,
    model,
    trim,
    fuelType,
  } = aftermarketPurchaseFromQuery;

  const { promo, skipToQuoteForm } = router.query;

  const provincesList = useProvincesStatusBySourceList(
    querySource,
    provinceFilters.isAftermarketEnabled
  );

  const { maskPipe: numberMaskPipe } = useNullableNumberMask();

  const { fuelTypes } = useVehicleDisplayFuelTypes();
  const fuelTypeOptions: { id: number; name: string }[] | null = useMemo(
    () =>
      fuelTypes.length > 0
        ? fuelTypes.map(({ id, name }) => ({
            id,
            name: translate(name, locale),
          }))
        : null,
    [fuelTypes, locale]
  );

  const methods = useForm<WarrantySegmentationFormProps>({
    shouldFocusError: false,
    defaultValues: {
      firstName: firstName ?? "",
      lastName: lastName ?? "",
      email: email ?? "",
      phone: phone ?? "",
      province: buyerProvince
        ? provincesList.find((p) => p.abbr === buyerProvince)
        : undefined,
      vin: buyerVin ?? "",
      odometer: mileage ? numberMaskPipe(mileage.toString()) : "",
      consent: false,
      yearMakeModel:
        year && make && model && model !== "Other" && model !== "Autre"
          ? {
              year,
              make,
              model,
              trim: trim ?? undefined,
              name: getCondensedLookupVehicleName(year, make, model, trim),
            }
          : undefined,
      fuelType: fuelType
        ? fuelTypeOptions?.find((ft) => ft.name === fuelType)
        : undefined,
      promoCode: (promo as string)?.toUpperCase(),
    },
  });

  const [firstLoad, setFirstLoad] = useState(true);

  useEffect(() => {
    const aftermarketPurchaseFromQueryHasData = !!(
      firstName ||
      lastName ||
      email ||
      phone ||
      buyerProvince ||
      buyerVin ||
      mileage ||
      year ||
      make ||
      model ||
      trim ||
      fuelType
    );
    if (router.isReady && firstLoad && aftermarketPurchaseFromQueryHasData) {
      methods.reset({
        firstName: firstName ?? "",
        lastName: lastName ?? "",
        email: email ?? "",
        phone: phone ?? "",
        province: buyerProvince
          ? provinces.find((p) => p.abbr === buyerProvince)
          : undefined,
        vin: buyerVin ?? "",
        odometer: mileage ? numberMaskPipe(mileage.toString()) : "",
        consent: false,
        yearMakeModel:
          year && make && model && model !== "Other" && model !== "Autre"
            ? {
                year,
                make,
                model,
                trim: trim ?? undefined,
                name: getCondensedLookupVehicleName(year, make, model, trim),
              }
            : undefined,
        fuelType: fuelType
          ? fuelTypeOptions?.find((ft) => ft.name === fuelType)
          : undefined,
        promoCode: (promo as string)?.toUpperCase(),
      });
      setFirstLoad(false);
    }
  }, [
    buyerProvince,
    buyerVin,
    email,
    firstLoad,
    firstName,
    fuelType,
    fuelTypeOptions,
    lastName,
    make,
    methods,
    mileage,
    model,
    numberMaskPipe,
    phone,
    promo,
    router.isReady,
    trim,
    year,
  ]);

  useEffect(() => {
    if (skipToQuoteForm) {
      switchMode(quoteVehicleFormMode);
      removeQueryParams(router, ["skipToQuoteForm"]);
    }
  }, [router, skipToQuoteForm, switchMode]);

  const headingText = useMemo(() => {
    switch (mode.name) {
      case WarrantySegmentationModeName.quoteVehicleForm:
      case WarrantySegmentationModeName.quotePersonalForm:
        return t("purchase:lets_get_started");
      case WarrantySegmentationModeName.estimatedCoverageOption:
        return t("purchase:get_estimated_coverage_options");
      default:
        return null;
    }
  }, [mode.name, t]);

  const subText = useMemo(() => {
    switch (mode.name) {
      case WarrantySegmentationModeName.quoteVehicleForm:
        return t("purchase:enter_your_vehicle_details_below");
      case WarrantySegmentationModeName.quotePersonalForm:
        return t("purchase:just_enter_your_details_below");
      default:
        return null;
    }
  }, [mode.name, t]);

  return (
    <div>
      <div className="flex flex-col items-center justify-center sm:px-1 md:px-6">
        <div className="w-full max-w-md">
          <h3 className="mb-6 justify-center text-center">{headingText}</h3>
          <div className="mb-6 justify-center text-center text-lg font-normal">
            {subText}
          </div>
        </div>
      </div>
      {/* eslint-disable react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <div key={mode.name}>
          {mode.name === WarrantySegmentationModeName.quoteVehicleForm && (
            <WarrantyQuoteVehicleInfoForm
              onChangeMode={switchMode}
              setVehicleSearchMode={setVehicleSearchMode}
              vehicleSearchMode={vehicleSearchMode}
            />
          )}
          {mode.name === WarrantySegmentationModeName.quotePersonalForm && (
            <WarrantyQuotePersonalInfoForm
              onChangeMode={switchMode}
              vehicleSearchMode={vehicleSearchMode}
            />
          )}
        </div>
      </FormProvider>
    </div>
  );
};

export default WarrantySegmentationForm;
